import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { useForm } from "@inertiajs/react"
import { Checkbox } from "@/components/ui/checkbox"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

const RESERVED_SLUGS = ["app", "www", "api", "admin", "mail", "smtp", "imap"]

function validate(data) {
  const errs = {}
  if (!data.name.trim()) errs.name = "Name can't be blank"
  if (data.slug) {
    if (!/^[a-z0-9-]+$/.test(data.slug)) errs.slug = "Only lowercase letters, numbers, and hyphens"
    else if (RESERVED_SLUGS.includes(data.slug)) errs.slug = "This slug is reserved and cannot be used"
  }
  return errs
}

export function WorkspaceForm({ workspace, primary, onSubmit, submitLabel }) {
  const isEdit = !!workspace?.id
  const [clientErrors, setClientErrors] = useState({})

  const { data, setData, processing, errors: serverErrors } = useForm({
    name: workspace?.name || "",
    slug: workspace?.slug || "",
    primary: primary || false,
  })

  const errors = { ...clientErrors, ...serverErrors }

  function handleSubmit(e) {
    e.preventDefault()
    const errs = validate(data)
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs)
      return
    }
    setClientErrors({})
    onSubmit(data)
  }

  function handleChange(field, value) {
    setData(field, value)
    if (clientErrors[field]) setClientErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-md">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="name">Name</FieldLabel>
          <Input
            type="text"
            id="name"
            value={data.name}
            aria-invalid={!!errors.name}
            className={errors.name ? "border-destructive" : ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <AnimatePresence>
            {errors.name && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <FieldError>{errors.name}</FieldError>
              </motion.div>
            )}
          </AnimatePresence>
        </Field>

        <Field>
          <FieldLabel htmlFor="slug">Slug</FieldLabel>
          <Input
            type="text"
            id="slug"
            value={data.slug}
            aria-invalid={!!errors.slug}
            className={errors.slug ? "border-destructive" : ""}
            onChange={(e) => handleChange("slug", e.target.value)}
          />
          <FieldDescription>Leave blank to auto-generate from name</FieldDescription>
          <AnimatePresence>
            {errors.slug && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <FieldError>{errors.slug}</FieldError>
              </motion.div>
            )}
          </AnimatePresence>
        </Field>

        {isEdit && (
          <Field>
            <div className="flex items-start gap-2">
              <Checkbox
                id="primary"
                checked={data.primary}
                onCheckedChange={(checked) => setData("primary", checked)}
                className="mt-0.5"
              />
              <div>
                <FieldLabel htmlFor="primary">Primary workspace</FieldLabel>
                <FieldDescription>Agents created via email will be added to your primary workspace</FieldDescription>
              </div>
            </div>
          </Field>
        )}

        <Field className="w-fit">
          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : submitLabel}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  )
}
